import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Receipt, BarChart3, Package, 
  ArrowRightLeft, Building2, Bot, Settings, Users, LogOut, 
  Menu, X, ChevronDown, Sparkles, FileCheck, Crown, Store, 
  Calculator, FolderKanban, RefreshCw, UserSquare2, Car, 
  Globe, ShoppingCart, ClipboardList, HeadphonesIcon, UserPlus,
  ChevronLeft, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getFinancePermissions } from '@/utils/rolePermissions';

// Define navigation with permission requirements
const getNavigationItems = (permissions) => [
  { name: 'Dashboard', href: '/suite/finance', icon: LayoutDashboard, exact: true, show: true },
  { name: 'Invoices', href: '/suite/finance/invoices', icon: FileText, show: permissions.canCreateInvoice || permissions.canViewReports },
  { name: 'Recurring Invoices', href: '/suite/finance/recurring-invoices', icon: RefreshCw, show: permissions.canCreateInvoice },
  { name: 'Estimates/Quotes', href: '/suite/finance/estimates', icon: ClipboardList, show: permissions.canCreateInvoice },
  { name: 'Customers', href: '/suite/finance/customers', icon: UserSquare2, show: permissions.canManageCustomers },
  { name: 'E-Invoice (LHDN)', href: '/suite/finance/e-invoice', icon: FileCheck, show: permissions.canCreateInvoice },
  { name: 'Expenses', href: '/suite/finance/expenses', icon: Receipt, show: true },
  { name: 'Bills', href: '/suite/finance/bills', icon: FileText, show: permissions.canManageVendors },
  { name: 'Vendors', href: '/suite/finance/vendors', icon: Store, show: permissions.canManageVendors },
  { name: 'Purchase Orders', href: '/suite/finance/purchase-orders', icon: ShoppingCart, show: permissions.canManageVendors },
  { name: 'Payroll', href: '/suite/finance/payroll', icon: Calculator, show: permissions.canViewPayroll },
  { name: 'Projects & Time', href: '/suite/finance/projects', icon: FolderKanban, show: true },
  { name: 'Mileage', href: '/suite/finance/mileage', icon: Car, show: true },
  { name: 'Reports', href: '/suite/finance/reports', icon: BarChart3, show: permissions.canViewReports },
];

const aiNavigation = [
  { name: 'AI Assistant', href: '/suite/finance/ai-assistant', icon: Bot },
  { name: 'AI Tools', href: '/suite/finance/ai-tools', icon: Sparkles },
];

const adminNavigation = [
  { name: 'Users', href: '/suite/finance/users', icon: Users },
  { name: 'Settings', href: '/suite/finance/settings', icon: Settings },
];

export default function FinanceLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const financeUserStr = localStorage.getItem('financeUser');
  const user = financeUserStr ? JSON.parse(financeUserStr) : userData;
  
  // Get role-based permissions
  const permissions = getFinancePermissions(user.finance_role || user.role);
  const navigation = getNavigationItems(permissions).filter(item => item.show);

  const handleLogout = () => {
    localStorage.removeItem('financeUser');
    localStorage.removeItem('financeToken');
    navigate('/portal');
  };

  const isAdmin = user.role === 'super_admin' || user.role === 'platform_admin' || user.role === 'finance_admin' || permissions.canManageSettings;

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-slate-900 border-r border-slate-800
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal')}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 mb-3"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            All Products
          </Button>
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">MyFinance Orbit</h2>
              <p className="text-xs text-slate-400">AI-Powered Accounting</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Main Navigation */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-3">Main</p>
            <div className="space-y-1">
              {navigation.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.exact}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-emerald-600 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </NavLink>
              ))}
            </div>
          </div>

          {/* AI Tools */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-3">AI Tools</p>
            <div className="space-y-1">
              {aiNavigation.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-emerald-600 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </NavLink>
              ))}
            </div>
          </div>

          {/* Admin */}
          {isAdmin && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-3">Admin</p>
              <div className="space-y-1">
                {adminNavigation.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-emerald-600 text-white'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          )}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start text-left">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium text-sm">
                    {(user.name || user.full_name || 'U')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user.name || user.full_name || 'User'}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {user.role || 'Finance User'}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
              <DropdownMenuItem 
                onClick={() => navigate('/suite/finance/settings')}
                className="text-slate-300 hover:bg-slate-700 cursor-pointer"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:bg-slate-700 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Back to Portal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-slate-900 border-b border-slate-800">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="text-slate-400"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <span className="font-semibold text-white">MyFinance Orbit</span>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-slate-950 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
